import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../utils/api";
import { API_ENDPOINTS } from "../utils/urls";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function EditProfile() {
  const [userData, setUserData] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});

  const getUserData = async () => {
    try {
      const res = await api.get(API_ENDPOINTS.USER.GET_USER);
      setUserData(res.data.data);
      setFormData({ ...res.data.data });
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to fetch user data");
    }
  };

  useEffect(() => {
    getUserData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSave = async () => {
    try {
      const updatedData = { ...formData };
      delete updatedData.username;
      delete updatedData.isActive;
      delete updatedData.isEmailVerified;

      const res = await api.put(API_ENDPOINTS.USER.UPDATE_USER, updatedData);

      if (res.data.error) {
        toast.error("Failed to update profile");
      } else {
        toast.success("Profile updated successfully");
        getUserData();
      }
    } catch (error) {
      toast.error("Error updating profile");
      console.error(error);
    }
  };

  const renderProfileStatus = () => {
    if (userData?.isActive) {
      return (
        <span className="bg-green-100 text-green-800 text-xs font-medium py-1 px-3 rounded-full">
          Profile Activated
        </span>
      );
    } else {
      return (
        <span className="bg-red-100 text-red-800 text-xs font-medium py-1 px-3 rounded-full">
          Profile Not Active, Contact Support
        </span>
      );
    }
  };
  const renderEmailStatus = () => {
    if (userData?.isEmailVerified) {
      return (
        <span className="bg-green-100 text-green-800 text-xs font-medium py-1 px-3 rounded-full">
          Email Verified
        </span>
      );
    } else {
      return (
        <span className="bg-red-100 text-red-800 text-xs font-medium py-1 px-3 rounded-full">
          Email Not Verified, Contact Support
        </span>
      );
    }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-3xl font-bold text-center mb-6">Edit Profile</h2>

          {/* Profile Thumbnail & Status */}
          <div className="flex items-center justify-start space-x-4 mb-8">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center">
                {/* SVG placeholder for profile image */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  className="w-16 h-16 text-white"
                >
                  <defs>
                    <style>{`.cls-3{fill:#6fabe6}.cls-4{fill:#82bcf4}`}</style>
                  </defs>
                  <g id="User_profile" data-name="User profile">
                    <path
                      d="M47 24A23 23 0 1 1 12.81 3.91 23 23 0 0 1 47 24z"
                      style={{ fill: "#374f68" }}
                    />
                    <path
                      d="M47 24a22.91 22.91 0 0 1-8.81 18.09A22.88 22.88 0 0 1 27 45C5.28 45-4.37 17.34 12.81 3.91A23 23 0 0 1 47 24z"
                      style={{ fill: "#425b72" }}
                    />
                    <path
                      className="cls-3"
                      d="M39.2 35.39a19 19 0 0 1-30.4 0 17 17 0 0 1 10.49-8.73 8.93 8.93 0 0 0 9.42 0 17 17 0 0 1 10.49 8.73z"
                    />
                    <path
                      className="cls-4"
                      d="M39.2 35.39a19 19 0 0 1-4.77 4.49 19 19 0 0 1-15.13-1 7.08 7.08 0 0 1-.51-12.18c.1-.07 0-.05.5-.05a9 9 0 0 0 9.42 0 17 17 0 0 1 10.49 8.74z"
                    />
                    <path
                      className="cls-3"
                      d="M33 19a9 9 0 1 1-12.38-8.34A9 9 0 0 1 33 19z"
                    />
                    <path
                      className="cls-4"
                      d="M33 19a9 9 0 0 1-2.6 6.33c-9.13 3.74-16.59-7.86-9.78-14.67A9 9 0 0 1 33 19z"
                    />
                  </g>
                </svg>
              </div>
            </div>
            <div>
              <p className="text-xl font-semibold">{formData.name}</p>
              <p className="text-sm text-gray-500">{formData.username}</p>
              <div className="mt-2">{renderProfileStatus()}</div>
              <div className="mt-2">{renderEmailStatus()}</div>
            </div>
          </div>

          {/* Editable Profile Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold">Profile Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phonenumber"
                    value={formData.phonenumber}
                    onChange={handleChange}
                    className="mt-2 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Store Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Store Name
                  </label>
                  <input
                    type="text"
                    name="storeName"
                    value={formData.storeName}
                    onChange={handleChange}
                    className="mt-2 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Address */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="mt-2 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* GST */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    GST
                  </label>
                  <input
                    type="text"
                    name="GST"
                    value={formData.GST}
                    onChange={handleChange}
                    className="mt-2 w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
